import React, { useState, useEffect, useRef, useCallback, ChangeEvent, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { Session } from '@/types'
import { supabase } from '../../../lib/supabase'
import { useModalBehavior } from '../../../hooks/useModalBehavior'
import CustomConfirmModal from '../../common/CustomConfirmModal'
import styles from './MessagesSection.module.css'

type ConversationType = 'general' | 'borrow_request' | 'comment'
type FilterType = 'all' | ConversationType

interface FilterOption {
    value: string
    label: string
}

interface FilterDropdownProps {
    label: string
    options: FilterOption[]
    selectedValues: string[]
    onSelect: (values: string[]) => void
    onClear: () => void
    isOpen: boolean
    onToggle: (open: boolean) => void
}

function FilterDropdown({ label, options, selectedValues, onSelect, onClear, isOpen, onToggle }: Readonly<FilterDropdownProps>) {
    const modalRef = useRef<HTMLDivElement>(null)

    useModalBehavior({
        modalRef,
        onClose: () => isOpen && onToggle(false),
    })

    const toggleOption = (value: string) => {
        if (selectedValues.includes(value)) {
            onSelect(selectedValues.filter((v) => v !== value))
        } else {
            onSelect([...selectedValues, value])
        }
    }

    const hasSelection = selectedValues.length > 0

    return (
        <div className={`${styles.filterDropdownContainer} ${isOpen ? styles.isOpen : ''}`} ref={modalRef}>
            <button className={`${styles.filterInputTrigger} ${hasSelection ? styles.active : ''}`} onClick={() => onToggle(!isOpen)} type="button">
                <span className={styles.truncate}>{hasSelection ? `${selectedValues.length} seleccionados` : label}</span>
                <span>{isOpen ? '▲' : '▼'}</span>
            </button>

            {isOpen && (
                <div className={styles.filterDropdownMenu}>
                    {hasSelection && (
                        <div
                            className={styles.filterClearItem}
                            onClick={(e: MouseEvent) => {
                                e.stopPropagation()
                                onClear()
                            }}
                        >
                            ✕ Limpiar
                        </div>
                    )}
                    {options.length === 0 ? (
                        <div style={{ padding: '8px', color: 'var(--text-tertiary)', textAlign: 'center' }}>No hay usuarios</div>
                    ) : (
                        options.map((opt) => (
                            <label key={opt.value}>
                                <input type="checkbox" checked={selectedValues.includes(opt.value)} onChange={() => toggleOption(opt.value)} />
                                <span>{opt.label}</span>
                            </label>
                        ))
                    )}
                </div>
            )}
        </div>
    )
}

interface UserProfile {
    id: string
    email: string
    full_name?: string | null
    avatar_url?: string | null
}

interface Message {
    id: string
    conversation_id: string
    sender_id: string
    content: string
    created_at: string
}

interface Conversation {
    id: string
    participant1_id: string
    participant2_id: string
    type?: ConversationType
    metadata?: {
        book_titles?: string[]
    }
    created_at: string
    updated_at: string
    participant1?: UserProfile
    participant2?: UserProfile
    messages: Message[]
    otherUser?: UserProfile
    visibleMessages?: Message[]
    partnerLastRead: string
}

interface MessagesSectionProps {
    session: Session
}

export default function MessagesSection({ session }: Readonly<MessagesSectionProps>) {
    const { t, i18n } = useTranslation()
    const [conversations, setConversations] = useState<Conversation[]>([])
    const [participatingUsers, setParticipatingUsers] = useState<UserProfile[]>([])
    const [selectedRecipient, setSelectedRecipient] = useState('')
    const [newMessage, setNewMessage] = useState('')
    const [loading, setLoading] = useState(true)
    const [creating, setCreating] = useState(false)

    const [replyModalOpen, setReplyModalOpen] = useState(false)
    const [replyConversationId, setReplyConversationId] = useState<string | null>(null)
    const [replyMessage, setReplyMessage] = useState('')
    const [sendingReply, setSendingReply] = useState(false)

    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [conversationToDelete, setConversationToDelete] = useState<string | null>(null)

    const [lastSeenMessagesAt, setLastSeenMessagesAt] = useState<string | null>(null)

    const [filterUsers, setFilterUsers] = useState<string[]>([])
    const [filterType, setFilterType] = useState<FilterType>('all')
    const [filterDateStart, setFilterDateStart] = useState('')
    const [filterDateEnd, setFilterDateEnd] = useState('')

    const [isUserFilterOpen, setIsUserFilterOpen] = useState(false)

    const fetchPotentialRecipients = useCallback(async () => {
        try {
            const { data: viewers } = await supabase
                .from('library_shares')
                .select('viewer_id, profiles:viewer_id(id, email, full_name)')
                .eq('owner_id', session.user.id)
                .eq('status', 'accepted')

            const { data: owners } = await supabase
                .from('library_shares')
                .select('owner_id, profiles:owner_id(id, email, full_name)')
                .eq('viewer_id', session.user.id)
                .eq('status', 'accepted')

            const potentialUsers: UserProfile[] = []
            const seenIds = new Set<string>()

            interface ShareWithProfile {
                viewer_id?: string
                owner_id?: string
                profiles: UserProfile | UserProfile[] | null
            }

            const viewersRaw = viewers as unknown as ShareWithProfile[]
            const ownersRaw = owners as unknown as ShareWithProfile[]

            viewersRaw?.forEach((item) => {
                const profile = Array.isArray(item.profiles) ? item.profiles[0] : item.profiles
                if (profile && !seenIds.has(profile.id)) {
                    potentialUsers.push(profile)
                    seenIds.add(profile.id)
                }
            })
            ownersRaw?.forEach((item) => {
                const profile = Array.isArray(item.profiles) ? item.profiles[0] : item.profiles
                if (profile && !seenIds.has(profile.id)) {
                    potentialUsers.push(profile)
                    seenIds.add(profile.id)
                }
            })

            setParticipatingUsers(potentialUsers)
        } catch (error) {
            console.error('Error fetching recipients:', error)
        }
    }, [session.user.id])

    const fetchConversations = useCallback(async () => {
        try {
            const { data: convData, error: convError } = await supabase
                .from('conversations')
                .select(
                    `
                    *,
                    participant1:participant1_id(id, email, full_name, avatar_url),
                    participant2:participant2_id(id, email, full_name, avatar_url)
                `
                )
                .order('updated_at', { ascending: false })

            if (convError) throw convError

            if (!convData || convData.length === 0) {
                setConversations([])
                return
            }

            const conversationIds = convData.map((c) => c.id)
            const { data: messagesData, error: msgError } = await supabase
                .from('messages')
                .select('*')
                .in('conversation_id', conversationIds)
                .order('created_at', { ascending: false })

            if (msgError) throw msgError

            const otherUserIds = convData.map((c) => (c.participant1_id === session.user.id ? c.participant2_id : c.participant1_id))

            const { data: partnerSettings } = await supabase
                .from('shared_library_settings')
                .select('user_id, last_seen_messages_at')
                .in('user_id', otherUserIds)

            const partnerLastSeenMap: Record<string, string> = {}
            partnerSettings?.forEach((s: { user_id: string; last_seen_messages_at: string }) => {
                partnerLastSeenMap[s.user_id] = s.last_seen_messages_at
            })

            const enrichedConversations = (convData as Conversation[]).map((conv): Conversation => {
                const msgs = messagesData?.filter((m) => m.conversation_id === conv.id) || []
                const other = conv.participant1_id === session.user.id ? conv.participant2 : conv.participant1 || conv.participant2

                const partnerLastRead = partnerLastSeenMap[other.id] || '1970-01-01T00:00:00Z'

                return {
                    ...conv,
                    messages: msgs,
                    otherUser: other,
                    partnerLastRead,
                }
            })

            setConversations(enrichedConversations)
        } catch (error) {
            console.error('Error fetching conversations:', error)
        }
    }, [session.user.id])

    const fetchInitialData = useCallback(async () => {
        setLoading(true)
        await Promise.all([fetchConversations(), fetchPotentialRecipients()])
        setLoading(false)
    }, [fetchConversations, fetchPotentialRecipients])

    const updateLastSeenDb = useCallback(async () => {
        try {
            const now = new Date().toISOString()
            const { error } = await supabase.from('shared_library_settings').upsert(
                {
                    user_id: session.user.id,
                    last_seen_messages_at: now,
                },
                { onConflict: 'user_id' }
            )

            if (error) console.error('Error updating last seen:', error)
        } catch (e) {
            console.error('Error updating last seen:', e)
        }
    }, [session.user.id])

    const fetchLastSeen = useCallback(async () => {
        const { data } = await supabase.from('shared_library_settings').select('last_seen_messages_at').eq('user_id', session.user.id).single()

        const timestamp = data?.last_seen_messages_at || '1970-01-01T00:00:00Z'
        setLastSeenMessagesAt(timestamp)

        updateLastSeenDb()
    }, [session.user.id, updateLastSeenDb])

    useEffect(() => {
        fetchInitialData()
        fetchLastSeen()
    }, [fetchInitialData, fetchLastSeen])

    const handleStartConversation = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!selectedRecipient || !newMessage.trim()) return

        setCreating(true)
        try {
            const { data: conv, error: convError } = await supabase
                .from('conversations')
                .insert({
                    participant1_id: session.user.id,
                    participant2_id: selectedRecipient,
                })
                .select()
                .single()

            if (convError) throw convError

            const { error: msgError } = await supabase.from('messages').insert({
                conversation_id: conv.id,
                sender_id: session.user.id,
                content: newMessage.trim(),
            })

            if (msgError) throw msgError

            setNewMessage('')
            setSelectedRecipient('')
            await fetchConversations()
        } catch (error) {
            console.error('Error starting conversation:', error)
            alert(t('biblionautas.messages.errors.send'))
        } finally {
            setCreating(false)
        }
    }

    const confirmDeleteConversation = (conversationId: string) => {
        setConversationToDelete(conversationId)
        setDeleteModalOpen(true)
    }

    const handleExecuteDelete = async () => {
        if (!conversationToDelete) return

        try {
            const { error } = await supabase.rpc('delete_conversation', { conversation_id: conversationToDelete })
            if (error) throw error

            setConversations((prev) => prev.filter((c) => c.id !== conversationToDelete))
            setDeleteModalOpen(false)
            setConversationToDelete(null)
        } catch (error) {
            console.error('Error deleting conversation:', error)
            alert(t('biblionautas.messages.errors.delete'))
        }
    }

    const openReplyModal = (conversationId: string) => {
        setReplyConversationId(conversationId)
        setReplyMessage('')
        setReplyModalOpen(true)
    }

    const closeReplyModal = () => {
        setReplyModalOpen(false)
        setReplyConversationId(null)
    }

    const handleSendReply = async () => {
        if (!replyMessage.trim()) return

        setSendingReply(true)
        try {
            const { error: msgError } = await supabase.from('messages').insert({
                conversation_id: replyConversationId,
                sender_id: session.user.id,
                content: replyMessage.trim(),
            })

            if (msgError) throw msgError

            await fetchConversations()
            closeReplyModal()
        } catch (error) {
            console.error('Error sending reply:', error)
            alert(t('biblionautas.messages.errors.reply'))
        } finally {
            setSendingReply(false)
        }
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return ''
        return new Date(dateString).toLocaleString(i18n.language)
    }

    const filteredConversations = conversations
        .map((conv) => {
            let visibleMessages = conv.messages

            if (filterDateStart || filterDateEnd) {
                visibleMessages = visibleMessages.filter((msg) => {
                    const msgDate = new Date(msg.created_at)
                    if (filterDateStart) {
                        const startDate = new Date(filterDateStart)
                        startDate.setHours(0, 0, 0, 0)
                        if (msgDate < startDate) return false
                    }
                    if (filterDateEnd) {
                        const endDate = new Date(filterDateEnd)
                        endDate.setHours(23, 59, 59, 999)
                        if (msgDate > endDate) return false
                    }
                    return true
                })
            }

            return { ...conv, visibleMessages }
        })
        .filter((conv) => {
            if (filterUsers.length > 0) {
                if (!conv.otherUser || !filterUsers.includes(conv.otherUser.id)) {
                    return false
                }
            }

            if (filterType !== 'all') {
                const type = conv.type || 'general'
                if (filterType === 'general' && type !== 'general') return false
                if (filterType === 'borrow_request' && type !== 'borrow_request') return false
                if (filterType === 'comment' && type !== 'comment') return false
            }

            return !((filterDateStart || filterDateEnd) && (conv.visibleMessages?.length || 0) === 0)
        })

    const userOptions: FilterOption[] = participatingUsers.map((u) => {
        const namePart = u.full_name ? ` (${u.full_name})` : ''
        return {
            value: u.id,
            label: `${u.email}${namePart}`,
        }
    })

    return (
        <div className={styles.messagesSection}>
            <div className={styles.startConversationCard}>
                <h3>{t('biblionautas.messages.title')}</h3>
                <form onSubmit={handleStartConversation} className={styles.newMessageForm} data-testid="start-conversation-form">
                    <div className={styles.formGroup}>
                        <label>{t('biblionautas.messages.form.to')}</label>
                        <select value={selectedRecipient} onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedRecipient(e.target.value)} required data-testid="new-message-recipient-select">
                            <option value="">{t('biblionautas.messages.form.selectPlaceholder')}</option>
                            {participatingUsers.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.email} {user.full_name ? `(${user.full_name})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <textarea
                            value={newMessage}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNewMessage(e.target.value)}
                            placeholder={t('biblionautas.messages.form.contentPlaceholder')}
                            maxLength={1000}
                            required
                            data-testid="new-message-content"
                        />
                        <div className={styles.charCount}>
                            {t('biblionautas.messages.form.charCount', { current: newMessage.length })}
                        </div>
                    </div>
                    <button type="submit" className="primary-btn" disabled={creating} data-testid="send-message-btn">
                        {creating ? t('biblionautas.messages.form.sending') : t('biblionautas.messages.form.send')}
                    </button>
                </form>
            </div>

            <div className={styles.conversationsList}>
                <h3 data-testid="messages-history-title">{t('biblionautas.messages.historyTitle')}</h3>

                {/* Filters */}
                <div className={styles.messagesFilters}>
                    <div className={`${styles.filterGroup} ${isUserFilterOpen ? styles.activeDropdownGroup : ''}`} style={{ zIndex: isUserFilterOpen ? 100 : 'auto' }}>
                        <label>{t('biblionautas.messages.filters.userLabel')}</label>
                        <FilterDropdown
                            label={t('biblionautas.messages.filters.selectUsers')}
                            options={userOptions}
                            selectedValues={filterUsers}
                            onSelect={setFilterUsers}
                            onClear={() => setFilterUsers([])}
                            isOpen={isUserFilterOpen}
                            onToggle={setIsUserFilterOpen}
                        />
                    </div>
                    <div className={styles.filterGroup}>
                        <label>{t('biblionautas.messages.filters.typeLabel')}</label>
                        <select value={filterType} onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilterType(e.target.value as FilterType)}>
                            <option value="all">{t('biblionautas.messages.filters.types.all')}</option>
                            <option value="general">{t('biblionautas.messages.filters.types.general')}</option>
                            <option value="borrow_request">{t('biblionautas.messages.filters.types.borrowRequest')}</option>
                            <option value="comment">{t('biblionautas.messages.filters.types.comment')}</option>
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <label>{t('biblionautas.messages.filters.dateFrom')}</label>
                        <input type="date" value={filterDateStart} onChange={(e: ChangeEvent<HTMLInputElement>) => setFilterDateStart(e.target.value)} />
                    </div>
                    <div className={styles.filterGroup}>
                        <label>{t('biblionautas.messages.filters.dateTo')}</label>
                        <input type="date" value={filterDateEnd} onChange={(e: ChangeEvent<HTMLInputElement>) => setFilterDateEnd(e.target.value)} />
                    </div>
                    {(filterUsers.length > 0 || filterType !== 'all' || filterDateStart || filterDateEnd) && (
                        <button
                            className={styles.clearFiltersBtn}
                            onClick={() => {
                                setFilterUsers([])
                                setFilterType('all')
                                setFilterDateStart('')
                                setFilterDateEnd('')
                            }}
                        >
                            {t('biblionautas.messages.filters.clear')}
                        </button>
                    )}
                </div>

                {loading && <p>{t('biblionautas.messages.list.loading')}</p>}
                {!loading && filteredConversations.length === 0 && (
                    <p className="empty-state">{t('biblionautas.messages.list.empty')}</p>
                )}
                {!loading && filteredConversations.length > 0 && (
                    filteredConversations.map((conv) => (
                        <div key={conv.id} className={styles.conversationBlock} data-testid={`conversation-block-${conv.otherUser?.email}`}>
                            <div className={styles.conversationHeader}>
                                <div className="header-info">
                                    <span className={styles.conversationPartner}>
                                        {t('biblionautas.messages.list.conversationWith')} <strong>{conv.otherUser?.email || t('biblionautas.messages.list.unknownUser')}</strong>
                                    </span>
                                    {(conv.type === 'borrow_request' || conv.type === 'comment') && conv.metadata?.book_titles && (
                                        <div className="conversation-context-badge">
                                            {conv.participant1_id === session.user.id ? (
                                                <span>
                                                    {conv.type === 'borrow_request' ? t('biblionautas.messages.list.context.requested') : t('biblionautas.messages.list.context.commented')}
                                                    <strong>{conv.metadata.book_titles.join(', ')}</strong>
                                                </span>
                                            ) : (
                                                <span>
                                                    {conv.type === 'borrow_request' ? t('biblionautas.messages.list.context.requests') : t('biblionautas.messages.list.context.comments')}
                                                    <strong>{conv.metadata.book_titles.join(', ')}</strong>
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.conversationActions}>
                                    <button className={`${styles.replyBtn} ${styles.primaryBtn}`} onClick={() => openReplyModal(conv.id)} data-testid={`reply-message-btn-${conv.id}`}>
                                        {t('biblionautas.messages.list.actions.reply')}
                                    </button>
                                    <button className={styles.deleteBtn} onClick={() => confirmDeleteConversation(conv.id)} title={t('biblionautas.messages.list.actions.delete')} data-testid={`delete-conversation-btn-${conv.id}`}>
                                        🗑️
                                    </button>
                                </div>
                            </div>
                            <div className={styles.messagesContainer}>
                                {(conv.visibleMessages || conv.messages).map((msg) => {
                                    const isReceived = msg.sender_id !== session.user.id
                                    const isUnread = isReceived && new Date(msg.created_at) > new Date(lastSeenMessagesAt || '1970-01-01')

                                    const isReadByPartner = !isReceived && new Date(msg.created_at) < new Date(conv.partnerLastRead)

                                    return (
                                        <div key={msg.id} className={`${styles.messageItem} ${isReceived ? styles.received : styles.sent} ${isUnread ? styles.unread : ''}`}>
                                            <div className={styles.messageMeta}>
                                                <span className="sender-name">{isReceived ? conv.otherUser?.email || t('biblionautas.messages.list.sender.user') : t('biblionautas.messages.list.sender.you')}</span>
                                                <span className="message-date">{formatDate(msg.created_at)}</span>
                                            </div>
                                            <div className={styles.messageContent}>{msg.content}</div>
                                            {!isReceived && <div className={`${styles.readReceipt} ${isReadByPartner ? styles.read : styles.sent}`}>{isReadByPartner ? '✓✓' : '✓'}</div>}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Reply Modal */}
            {replyModalOpen && (
                <div className={styles.modalOverlay} data-testid="reply-modal-overlay">
                    <div className={`${styles.modalContent} ${styles.replyModal}`} data-testid="reply-modal">
                        <h3>{t('biblionautas.messages.replyModal.title')}</h3>
                        <div className={styles.formGroup}>
                            <textarea
                                value={replyMessage}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReplyMessage(e.target.value)}
                                placeholder={t('biblionautas.messages.replyModal.placeholder')}
                                maxLength={1000}
                                data-testid="reply-message-input"
                            />
                            <div className={styles.charCount} data-testid="reply-char-count">
                                {t('biblionautas.messages.form.charCount', { current: replyMessage.length })}
                            </div>
                        </div>
                        <div className={styles.modalActions}>
                            <button onClick={closeReplyModal} className="secondary-btn" data-testid="reply-modal-cancel-btn">
                                {t('biblionautas.messages.replyModal.cancel')}
                            </button>
                            <button onClick={handleSendReply} className="primary-btn" disabled={sendingReply} data-testid="reply-modal-send-btn">
                                {sendingReply ? t('biblionautas.messages.replyModal.sending') : t('biblionautas.messages.replyModal.send')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <CustomConfirmModal
                isOpen={deleteModalOpen}
                title={t('biblionautas.messages.deleteModal.title')}
                message={t('biblionautas.messages.deleteModal.message')}
                type="danger"
                confirmText={t('biblionautas.messages.deleteModal.confirm')}
                cancelText={t('biblionautas.messages.deleteModal.cancel')}
                onConfirm={handleExecuteDelete}
                onCancel={() => {
                    setDeleteModalOpen(false)
                    setConversationToDelete(null)
                }}
            />
        </div>
    )
}
